// Decode the (signed, unencrypted) nextsub access token client-side to read
// identity + expiry. Format: base64url(JSON payload) "." base64url(hmac).
// Payload shape mirrors accessPayload in internal/authn/token.go.
export interface TokenClaims {
  userId: string;
  tenantId: string;
  tokenVersion: number;
  /** Expiry as Unix seconds. */
  exp: number;
  kid: string;
}

interface RawPayload {
  user_id: string;
  tenant_id: string;
  token_version: number;
  exp: number;
  kid: string;
}

function base64UrlDecode(input: string): string {
  const padded = input.replace(/-/g, "+").replace(/_/g, "/");
  const withPad = padded + "=".repeat((4 - (padded.length % 4)) % 4);
  return atob(withPad);
}

/** Decode access-token claims, or null if the token is malformed. */
export function decodeAccessToken(token: string): TokenClaims | null {
  const dot = token.indexOf(".");
  if (dot <= 0) return null;
  try {
    const json = base64UrlDecode(token.slice(0, dot));
    const raw = JSON.parse(json) as RawPayload;
    if (!raw.user_id || !raw.tenant_id || typeof raw.exp !== "number") {
      return null;
    }
    return {
      userId: raw.user_id,
      tenantId: raw.tenant_id,
      tokenVersion: raw.token_version ?? 0,
      exp: raw.exp,
      kid: raw.kid ?? "",
    };
  } catch {
    return null;
  }
}

/** Milliseconds until the token expires (negative if already expired). */
export function msUntilExpiry(claims: TokenClaims, now = Date.now()): number {
  return claims.exp * 1000 - now;
}

/** True when the token is missing or within `skewMs` of expiry. */
export function isExpired(claims: TokenClaims | null, skewMs = 30_000, now = Date.now()): boolean {
  if (!claims) return true;
  return msUntilExpiry(claims, now) <= skewMs;
}
